import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Quote } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useBrandStories } from '@/hooks/useBrandStories';
import { Skeleton } from '@/components/ui/skeleton';

interface Story {
  id: string;
  title: string;
  description: string;
  author: string;
  role: string;
  image: string;
  backgroundImage: string;
}

const BrandStoriesCarousel = () => {
  const { data: brandStoriesData, isLoading } = useBrandStories();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  // Transform database data to match the expected format
  const stories: Story[] = brandStoriesData?.map(story => ({
    id: story.id,
    title: story.title,
    description: story.description || '',
    author: story.author,
    role: story.role,
    image: story.image_url || '',
    backgroundImage: story.background_image_url || ''
  })) || [];

  useEffect(() => {
    if (!isAutoPlaying || stories.length === 0) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % stories.length);
    }, 6000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, stories.length]);

  const nextStory = () => {
    if (stories.length === 0) return;
    setCurrentIndex((prev) => (prev + 1) % stories.length);
  };

  const prevStory = () => {
    if (stories.length === 0) return;
    setCurrentIndex((prev) => (prev - 1 + stories.length) % stories.length);
  };

  if (isLoading) {
    return (
      <section className="relative py-20 overflow-hidden bg-gradient-to-br from-muted/10 via-background to-muted/5">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="text-center mb-12">
            <Skeleton className="h-8 w-32 mx-auto mb-4" />
            <Skeleton className="h-12 w-64 mx-auto mb-4" />
          </div>
          <div className="relative max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
              <div className="space-y-6">
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-20 w-full" />
                <div className="flex items-center gap-4">
                  <Skeleton className="h-12 w-12 rounded-full" />
                  <div>
                    <Skeleton className="h-4 w-24 mb-2" />
                    <Skeleton className="h-3 w-20" />
                  </div>
                </div>
              </div>
              <Skeleton className="aspect-[4/3] rounded-2xl" />
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (!stories || stories.length === 0) {
    return null;
  }

  const currentStory = stories[currentIndex];

  return (
    <section 
      className="relative py-20 overflow-hidden bg-gradient-to-br from-muted/10 via-background to-muted/5"
      onMouseEnter={() => setIsAutoPlaying(false)}
      onMouseLeave={() => setIsAutoPlaying(true)}
    >
      <div className="w-full px-4 md:px-8 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-full mb-4">
            <Quote className="h-4 w-4" />
            <span className="text-sm font-medium">Our Story</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Brand Stories</h2>
        </motion.div>

        <div className="relative max-w-6xl mx-auto">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStory.id}
              initial={{ opacity: 0, x: 100 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -100 }}
              transition={{ duration: 0.6, ease: "easeInOut" }}
              className="relative"
            >
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
                {/* Content */}
                <motion.div 
                  className="space-y-6"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2, duration: 0.6 }}
                >
                  <div className="relative">
                    <Quote className="absolute -top-4 -left-2 h-8 w-8 text-primary/20" />
                    <h3 className="text-2xl md:text-3xl font-bold text-foreground mb-4 pl-8">
                      {currentStory.title}
                    </h3>
                  </div>
                  
                  <p className="text-lg text-muted-foreground leading-relaxed">
                    {currentStory.description}
                  </p>
                  
                  <div className="flex items-center gap-4 pt-4">
                    {currentStory.image && (
                      <motion.img
                        src={currentStory.image}
                        alt={currentStory.author}
                        className="w-12 h-12 rounded-full object-cover border-2 border-primary/20"
                        whileHover={{ scale: 1.1 }}
                      />
                    )}
                    <div>
                      <p className="font-semibold text-foreground">{currentStory.author}</p>
                      <p className="text-sm text-muted-foreground">{currentStory.role}</p>
                    </div>
                  </div>
                </motion.div>

                {/* Image */}
                <motion.div
                  className="relative aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.3, duration: 0.6 }}
                  whileHover={{ scale: 1.02 }}
                >
                  {currentStory.backgroundImage ? (
                    <img
                      src={currentStory.backgroundImage}
                      alt={currentStory.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-primary/20 to-primary/5" />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
                </motion.div>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Navigation */}
          <div className="flex items-center justify-between mt-8">
            <Button
              variant="outline"
              size="sm"
              onClick={prevStory}
              className="rounded-full"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>

            {/* Indicators */}
            <div className="flex gap-2">
              {stories.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    index === currentIndex 
                      ? 'bg-primary w-6' 
                      : 'bg-muted-foreground/30 hover:bg-muted-foreground/50'
                  }`}
                />
              ))}
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={nextStory}
              className="rounded-full"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BrandStoriesCarousel;